<script>
    import {spring} from 'svelte/motion';
    import {fly} from 'svelte/transition';

    import classnames from 'classnames';

    import {movable} from '@js/components/themes/actions/mouse';

    const {
        class: className,
        // color = 'primary', // primary | secondary
        min = 0,
        max = 100,
        step = 1,
        marks,
        initialValue = [],
        valueLabelFormat,
        disabled = false,
        onChange,
        ...otherProps
    } = $props();

    const decimalCount = (val) => {
        return val?.toString()
            .split('.')[1]?.length ?? 0;
    };

    const round = (val, decimals) => {
        return Number(val.toFixed(decimals));
    };

    const scale = (val) => {
        return (val - min) / (max - min);
    };

    const isArrayEquals = (array1, array2) => {
        return Array.isArray(array1) &&
            Array.isArray(array2) &&
            array1.length === array2.length &&
            array1.every((val, index) => val === array2[index]);
    };

    const stepPercent = $derived(step / (max - min));
    const stepDecimals = decimalCount(step);

    let isMoving = false;
    let ignoreClickEvents = false;
    let previousValue = [initialValue[0] || min, initialValue[1] || max];

    const normalizedInitialValue = $derived([initialValue[0] || min, initialValue[1] || max]);

    let lastAdoptedInitialValue;

    let value = $state([initialValue[0] || min, initialValue[1] || max]);
    let lastMoved = $state('range'); // 'start' | 'range' | 'end'
    let showStartValue = $state(false);
    let showEndValue = $state(false);
    const start = $state(spring(0));
    const end = $state(spring(0));

    const applyMove = (which/*'start' | 'range' | 'end'*/, deltaValue) => {
        const [currentStartValue, currentEndValue] = value;
        // Round to fix float math (ex. `0.1 + 0.2` or `0.3 - 0.1`)
        const newStartValue = round(currentStartValue + deltaValue, stepDecimals);
        const newEndValue = round(currentEndValue + deltaValue, stepDecimals);

        switch (which) {
            case 'start':
                if (newStartValue >= min && newStartValue <= max) {
                    value = [newStartValue, Math.max(currentEndValue, newStartValue)];
                } else {
                    // Ignore
                }
                break;

            case 'range':
                if (newStartValue >= min && newEndValue <= max) {
                    value = [newStartValue, newEndValue];
                } else {
                    // Ignore
                }
                break;

            case 'end':
                if (newEndValue >= min && newEndValue <= max) {
                    value = [Math.min(newEndValue, currentStartValue), newEndValue];
                } else {
                    // Ignore
                }
                break;
        }
    };

    const onMoveStart = (which /*'start' | 'range' | 'end'*/) => (
        function () {
            ignoreClickEvents = true;

            isMoving = true;
            switch (which) {
                case 'start':
                    lastMoved = 'start';
                    showStartValue = true;
                    break;

                case 'range':
                    lastMoved = 'range';
                    showStartValue = true;
                    showEndValue = true;
                    break;

                case 'end':
                    lastMoved = 'end';
                    showEndValue = true;
                    break;
            }
        }
    );

    const onMove = (which /*'start' | 'range' | 'end'*/) => (
        function (event) {
            const parentEl = event.target.parentElement;
            const parentRect = parentEl.getBoundingClientRect();

            const deltaPercent = event.detail.dx / parentRect.width;
            const deltaValue = (max - min) * deltaPercent;

            applyMove(which, deltaValue);
        }
    );

    const onMoveEnd = () => (
        function () {
            // Ignore immediate click events after a drag (also fired on mouse up)
            setTimeout(() => {
                ignoreClickEvents = false;
            }, 100);

            isMoving = false;
            showStartValue = false;
            showEndValue = false;
        }
    );

    const onMouseEnter = (which) => (
        function () {
            if (isMoving === false) {
                switch (which) {
                    case 'start':
                        showStartValue = true;
                        break;

                    case 'range':
                        showStartValue = true;
                        showEndValue = true;
                        break;

                    case 'end':
                        showEndValue = true;
                        break;
                }
            }
        }
    );

    const onMouseLeave = () => (
        function () {
            if (isMoving === false) {
                showStartValue = false;
                showEndValue = false;
            }
        }
    );

    const onKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowLeft':
                applyMove(lastMoved, -step);
                break;
            case 'ArrowRight':
                applyMove(lastMoved, step);
                break;
        }
    };

    const onClick = (event) => {
        // Focus for keyboard input
        event.target.focus();

        if (ignoreClickEvents) {
            return;
        }

        let sliderRect;
        if (event.target.classList.contains('RangeSlider')) {
            // Root / track
            sliderRect = event.target.getBoundingClientRect();
        } else if (event.target.classList.contains('range')) {
            // Range selection
            sliderRect = event.target.parentElement.getBoundingClientRect();
        } else {
            // Ignore clicks on thumbs, etc
            return;
        }

        const dx = event.clientX - sliderRect.x;
        const deltaPercent = dx / sliderRect.width;
        const newValue = min + (max - min) * deltaPercent;

        // Move value closest to clicked point
        if (Math.abs(value[0] - newValue) < Math.abs(value[1] - newValue)) {
            value = [round(newValue, stepDecimals), value[1]];
            lastMoved = 'start';
        } else {
            value = [value[0], round(newValue, stepDecimals)];
            lastMoved = 'end';
        }
    };

    const formatValue = (val) => (valueLabelFormat ? valueLabelFormat(val) : val);

    $effect(() => {
        const incoming = normalizedInitialValue;

        if (isArrayEquals(incoming, lastAdoptedInitialValue)) {
            return;
        }

        lastAdoptedInitialValue = incoming;
        previousValue = incoming;
        value = incoming;
    });

    $effect(() => {
        start.set(scale(value[0]));

        end.set(scale(value[1]));

        if (!isArrayEquals(previousValue, value)) {
            onChange(value);

            previousValue = value;
        }
    });
</script>

<div class={classnames(
    'group relative h-2 bg-primary-600/15 rounded-full select-none outline-hidden',
    disabled && 'pointer-events-none opacity-50',
    className
  )}
     style="--start: {$start}; --end: {$end};"
     {disabled}
     tabindex={disabled ? -1 : 0}
     role="slider"
     aria-labelledby="slider-label"
     aria-valuemin={min}
     aria-valuemax={max}
     aria-valuenow={value}
     onclick={onClick}
     onkeydown={onKeyDown}
     {...otherProps}>
    <div style="left: calc(var(--start) * 100%); right: calc((1 - var(--end)) * 100%);"
         class="range absolute top-0 bottom-0 bg-primary-600"
         role="button"
         tabindex="0"
         onmouseenter={onMouseEnter('range')}
         onmouseleave={onMouseLeave('range')}></div>

    <div use:movable={{ axis: 'x', stepPercent }}
         onmovestart={onMoveStart('start')}
         onmove={onMove('start')}
         onmoveend={onMoveEnd('start')}
         onmouseenter={onMouseEnter('start')}
         onmouseleave={onMouseLeave('start')}
         ondblclick={() => (value = [min, value[1]])}
         role="button"
         tabindex="0"
         style="left: calc(var(--start) * 100%);"
         class={classnames(
      'thumb',
      'absolute top-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2',
      'border bg-primary-800 rounded-full',
      'outline-6 outline-primary-600/30',
      'hover:outline hover:outline-primary-400/30',
      (lastMoved === 'start' || lastMoved === 'range') && 'group-focus:outline group-focus:outline-primary/40'
    )}></div>

    <div use:movable={{ axis: 'x', stepPercent }}
         onmovestart={onMoveStart('end')}
         onmove={onMove('end')}
         onmoveend={onMoveEnd('end')}
         onmouseenter={onMouseEnter('end')}
         onmouseleave={onMouseLeave('end')}
         ondblclick={() => (value = [value[0], max])}
         role="button"
         tabindex="0"
         style="left: calc(var(--end) * 100%);"
         class={classnames(
      'thumb',
      'absolute top-1/2 w-4 h-4 -translate-x-1/2 -translate-y-1/2',
      'border bg-primary-800 rounded-full outline-3',
      'outline-6 outline-primary-600/30',
      'hover:outline hover:outline-primary-400/30',
      (lastMoved === 'end' || lastMoved === 'range') && 'group-focus:outline group-focus:outline-primary/40'
    )}></div>

    {#if showStartValue}
        <output style="left: calc(var(--start) * 100%);"
                class="value absolute top-1 -translate-x-1/2 -translate-y-[150%] text-xs text-primary-content text-white bg-primary-600 rounded-full px-2 py-1 shadow"
                transition:fly={{ y: 4, duration: 300 }}>
            {formatValue(value[0])}
        </output>
    {/if}

    {#if showEndValue}
        <output style="left: calc(var(--end) * 100%);"
                class="value absolute top-1 -translate-x-1/2 -translate-y-[150%] text-xs text-primary-content text-white bg-primary-600 rounded-full px-2 py-1 shadow"
                transition:fly={{ y: 4, duration: 300 }}>
            {formatValue(value[1])}
        </output>
    {/if}

    {#if marks?.length}
        <span class="text-xs text-gray-500 dark:text-gray-400 absolute start-0 -left-2 -bottom-5">
            {formatValue(marks[0])}
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400 absolute end-0 -right-2 -bottom-5">
            {formatValue(marks[1])}
        </span>
    {/if}
</div>
